import type { PuzzlePlayer } from '../puzzles/types.ts';
import type {
  ActionError,
  ActionErrorCode,
  EventAudience,
  GameEvent,
  GameState,
  PlayerId,
  PlayerState,
  Role,
} from '../types.ts';

/** A draft: the state being changed plus the events produced so far by one action. */
export interface Ctx {
  s: GameState;
  events: GameEvent[];
}

/** Appends an event to the game log and to the events returned by the current action. */
export function emit(
  ctx: Ctx,
  type: string,
  data: Record<string, unknown> = {},
  audience: EventAudience = 'all',
  secret?: Record<string, unknown>,
): GameEvent {
  const ev: GameEvent = {
    id: ctx.s.nextEventId++,
    type,
    atMs: ctx.s.clockMs,
    audience,
    data,
    ...(secret ? { secret } : {}),
  };
  ctx.s.log.push(ev);
  ctx.events.push(ev);
  return ev;
}

export const fail = (code: ActionErrorCode, message: string): ActionError => ({ code, message });

export function playerOf(s: GameState, id: PlayerId): PlayerState | undefined {
  return s.players.find((p) => p.id === id);
}

export function mustPlayer(s: GameState, id: PlayerId): PlayerState {
  const p = playerOf(s, id);
  if (!p) throw new Error(`unknown player ${id}`);
  return p;
}

export function idsWithRole(s: GameState, ...roles: Role[]): PlayerId[] {
  return s.players.filter((p) => roles.includes(p.role)).map((p) => p.id);
}

export function roleOf(s: GameState, id: PlayerId): Role {
  return mustPlayer(s, id).role;
}

export function presentRoles(s: GameState): Role[] {
  return s.players.map((p) => p.role);
}

/** The slimmed-down player list that puzzle modules receive. */
export function puzzlePlayers(s: GameState): PuzzlePlayer[] {
  return s.players.map((p) => ({
    id: p.id,
    role: p.role,
    seat: p.seat,
    expelled: p.expelled,
    coins: p.powers.coins,
    petards: p.powers.petards,
  }));
}

export function activePlayers(s: GameState): PlayerState[] {
  return s.players.filter((p) => !p.expelled);
}
